import { css, html, LitElement, nothing } from 'lit';
import { demoGroups, registerDemoComponents } from './demos/index.js';
import { demoLayoutStyles } from './demos/demo-shared.js';
import { setLocalizeInjector } from '../common/localize-injector.js';
import { state } from 'lit/decorators.js';
import type { CemDeclaration } from './demos/demo-api-docs.js';
import type { DemoSection } from './demos/demo-shared.js';

const THEMES = [
  { value: 'light', text: 'Light', className: 'webmodule-theme-light' },
  { value: 'gray', text: 'Gray', className: 'webmodule-theme-gray' },
  { value: 'dark', text: 'Dark', className: 'webmodule-theme-dark' },
  { value: 'developer', text: 'Developer', className: 'webmodule-theme-dev' }
];

/**
 * The component lab: a small documentation site for the webmodule component
 * library. Renders one {@link DemoSection} at a time (hash-routed, e.g.
 * `#/button`) with a grouped, filterable sidebar, a theme switcher, and an
 * API reference generated from the custom elements manifest.
 *
 * Registered as `<component-app>`; served by `npm start` from `docs/`.
 */
export default class ComponentApp extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--webmodule-color-neutral-50);
        color: var(--webmodule-color-neutral-900);
        font-family: var(--webmodule-font-sans);
        font-size: var(--webmodule-font-size-medium);
        line-height: var(--webmodule-line-height-normal);
      }

      /* ---- header ---- */

      .app-header {
        position: sticky;
        top: 0;
        z-index: 10;
        display: flex;
        align-items: center;
        gap: var(--webmodule-spacing-large);
        padding: var(--webmodule-spacing-small) var(--webmodule-spacing-x-large);
        background: var(--webmodule-panel-background-color);
        border-bottom: solid 1px var(--webmodule-color-neutral-200);
        box-shadow: var(--webmodule-shadow-x-small);
      }

      .brand {
        display: flex;
        align-items: baseline;
        gap: var(--webmodule-spacing-small);
        white-space: nowrap;
      }

      .brand strong {
        font-size: var(--webmodule-font-size-large);
      }

      .brand span {
        color: var(--webmodule-color-neutral-500);
        font-size: var(--webmodule-font-size-small);
      }

      .header-tools {
        display: flex;
        align-items: center;
        gap: var(--webmodule-spacing-medium);
        margin-left: auto;
      }

      .header-tools webmodule-select {
        width: 10rem;
      }

      /* ---- layout ---- */

      .app-body {
        display: flex;
        align-items: flex-start;
      }

      /* ---- sidebar ---- */

      .sidebar {
        position: sticky;
        top: 3.4rem;
        flex: 0 0 15rem;
        max-height: calc(100vh - 3.4rem);
        overflow-y: auto;
        padding: var(--webmodule-spacing-large) var(--webmodule-spacing-medium) var(--webmodule-spacing-3x-large)
          var(--webmodule-spacing-large);
        box-sizing: border-box;
      }

      .sidebar webmodule-input {
        margin-bottom: var(--webmodule-spacing-large);
      }

      .nav-group-label {
        margin: var(--webmodule-spacing-large) 0 var(--webmodule-spacing-x-small) 0;
        font-size: var(--webmodule-font-size-x-small);
        font-weight: var(--webmodule-font-weight-semibold, 600);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--webmodule-color-neutral-500);
      }

      .nav-group-label:first-of-type {
        margin-top: 0;
      }

      .sidebar nav a {
        display: block;
        padding: 0.3rem var(--webmodule-spacing-medium);
        border-radius: var(--webmodule-border-radius-medium);
        color: var(--webmodule-color-neutral-700);
        font-size: var(--webmodule-font-size-small);
        text-decoration: none;
      }

      .sidebar nav a:hover {
        background: var(--webmodule-color-neutral-100);
        color: var(--webmodule-color-neutral-900);
      }

      .sidebar nav a.active {
        background: var(--webmodule-color-primary-100);
        color: var(--webmodule-color-primary-700);
        font-weight: var(--webmodule-font-weight-semibold, 600);
      }

      .no-matches {
        color: var(--webmodule-color-neutral-500);
        font-size: var(--webmodule-font-size-small);
      }

      /* ---- content ---- */

      main {
        flex: 1 1 auto;
        min-width: 0;
        max-width: 68rem;
        padding: var(--webmodule-spacing-x-large) var(--webmodule-spacing-2x-large) var(--webmodule-spacing-4x-large)
          var(--webmodule-spacing-2x-large);
        box-sizing: border-box;
      }

      .page-header {
        margin-bottom: var(--webmodule-spacing-x-large);
      }

      .page-header h1 {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--webmodule-spacing-medium);
        margin: 0 0 var(--webmodule-spacing-small) 0;
        font-size: var(--webmodule-font-size-2x-large);
        font-weight: var(--webmodule-font-weight-semibold, 600);
      }

      .tag-chip {
        font-family: var(--webmodule-font-mono);
        font-size: var(--webmodule-font-size-x-small);
        font-weight: var(--webmodule-font-weight-normal, 400);
        color: var(--webmodule-color-primary-700);
        background: var(--webmodule-color-primary-50);
        border: solid 1px var(--webmodule-color-primary-200);
        border-radius: var(--webmodule-border-radius-pill);
        padding: 0.15em 0.8em;
        white-space: nowrap;
      }

      .summary {
        margin: 0;
        max-width: 52rem;
        color: var(--webmodule-color-neutral-600);
      }

      .page-content {
        display: flex;
        flex-direction: column;
        gap: var(--webmodule-spacing-x-large);
      }

      .api {
        margin-top: var(--webmodule-spacing-2x-large);
      }

      .api h2 {
        font-size: var(--webmodule-font-size-large);
        font-weight: var(--webmodule-font-weight-semibold, 600);
        margin: 0 0 var(--webmodule-spacing-medium) 0;
      }

      .api demo-api-docs + demo-api-docs {
        margin-top: var(--webmodule-spacing-medium);
      }

      .api-note {
        color: var(--webmodule-color-neutral-500);
        font-size: var(--webmodule-font-size-small);
      }

      code {
        font-family: var(--webmodule-font-mono);
        font-size: 0.9em;
        background: var(--webmodule-color-neutral-100);
        border-radius: var(--webmodule-border-radius-small);
        padding: 0.1em 0.35em;
      }
    `
  ];

  @state() private activeId = '';
  @state() private filter = '';
  @state() private theme = 'gray';
  @state() private manifest: Map<string, CemDeclaration> | null = null;

  private readonly handleHashChange = () => {
    const id = window.location.hash.replace(/^#\/?/, '');

    if (id && this.findSection(id)) {
      this.activeId = id;
      this.renderRoot.querySelector('main')?.scrollIntoView({ block: 'start' });
    }
  };

  connectedCallback() {
    super.connectedCallback();
    registerDemoComponents();

    // The component lab has no localization backend, so components that read
    // localized strings (e.g. the table paginator) would throw. Install a
    // simple fallback. setLocalizeInjector only sets when unset, so this never
    // overrides a real app injector (and <component-app> only exists in the lab).
    setLocalizeInjector({
      interpolate: (id: string) => {
        const map: Record<string, string> = {
          'paginator.first': 'First',
          'paginator.prev': 'Prev',
          'paginator.next': 'Next',
          'paginator.last': 'Last',
          'paginator.loading': 'Loading…'
        };
        const key = id.split('.').slice(-2).join('.');
        return map[key] ?? id.split('.').pop() ?? id;
      }
    });

    window.addEventListener('hashchange', this.handleHashChange);
    const initial = window.location.hash.replace(/^#\/?/, '');
    this.activeId = initial && this.findSection(initial) ? initial : demoGroups[0].sections[0].id;
    this.applyTheme(this.theme);
    void this.loadManifest();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  render() {
    const section = this.findSection(this.activeId) ?? demoGroups[0].sections[0];

    return html`
      <header class="app-header">
        <div class="brand">
          <strong>WebModule UI</strong>
          <span>Component Lab</span>
        </div>
        <div class="header-tools">
          <webmodule-select
            size="small"
            .values=${THEMES.map(theme => ({ value: theme.value, text: theme.text }))}
            .value=${this.theme}
            @webmodule-change=${(e: Event) => this.applyTheme((e.target as HTMLInputElement).value)}
          >
            <span slot="prefix">Theme</span>
          </webmodule-select>
        </div>
      </header>

      <div class="app-body">
        <aside class="sidebar">
          <webmodule-input
            size="small"
            placeholder="Filter components"
            clearable
            .value=${this.filter}
            @webmodule-input=${(e: Event) => (this.filter = (e.target as HTMLInputElement).value)}
            @webmodule-clear=${() => (this.filter = '')}
          ></webmodule-input>
          ${this.renderNav()}
        </aside>

        <main>
          <div class="page-header">
            <h1>
              ${section.title}
              ${section.tags?.map(tag => html`<span class="tag-chip">&lt;${tag}&gt;</span>`) ?? nothing}
            </h1>
            ${section.summary ? html`<p class="summary">${section.summary}</p>` : nothing}
          </div>

          <div class="page-content">${section.render()}</div>

          ${this.renderApiReference(section)}
        </main>
      </div>
    `;
  }

  private renderNav() {
    const filter = this.filter.trim().toLowerCase();
    const groups = demoGroups
      .map(group => ({
        label: group.label,
        sections: group.sections.filter(section => !filter || section.title.toLowerCase().includes(filter))
      }))
      .filter(group => group.sections.length > 0);

    if (!groups.length) {
      return html`<p class="no-matches">No components match "${this.filter}".</p>`;
    }

    return html`
      <nav>
        ${groups.map(
          group => html`
            <div class="nav-group-label">${group.label}</div>
            ${group.sections.map(
              section => html`
                <a href="#/${section.id}" class=${section.id === this.activeId ? 'active' : ''}>${section.title}</a>
              `
            )}
          `
        )}
      </nav>
    `;
  }

  private renderApiReference(section: DemoSection) {
    if (!section.tags?.length) {
      return nothing;
    }

    return html`
      <div class="api">
        <h2>API Reference</h2>
        ${this.manifest
          ? section.tags.map(
              tag => html`<demo-api-docs tag=${tag} .declaration=${this.manifest!.get(tag) ?? null}></demo-api-docs>`
            )
          : html`<p class="api-note">
              Generated from <code>custom-elements.json</code> - not available in this session.
            </p>`}
      </div>
    `;
  }

  private findSection(id: string): DemoSection | undefined {
    for (const group of demoGroups) {
      const section = group.sections.find(candidate => candidate.id === id);

      if (section) {
        return section;
      }
    }

    return undefined;
  }

  private applyTheme(value: string) {
    const theme = THEMES.find(candidate => candidate.value === value) ?? THEMES[1];
    this.theme = theme.value;
    document.documentElement.classList.remove(...THEMES.map(candidate => candidate.className));
    document.documentElement.classList.add(theme.className);
  }

  private async loadManifest() {
    try {
      const response = await fetch('/distComponents/custom-elements.json');

      if (!response.ok) {
        return;
      }

      const manifest = (await response.json()) as {
        modules?: { declarations?: CemDeclaration[] }[];
      };
      const byTag = new Map<string, CemDeclaration>();

      for (const module of manifest.modules ?? []) {
        for (const declaration of module.declarations ?? []) {
          if (declaration.tagName) {
            byTag.set(declaration.tagName, declaration);
          }
        }
      }

      this.manifest = byTag;
    } catch {
      // The API reference is optional; the demos work without it.
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'component-app': ComponentApp;
  }
}
